import React, { useEffect } from 'react'
import { Modal } from 'antd'
import IntlMessages from '../../util/IntlMessages'
import { isUpgrade, isDowngrade, isSwitchingMonthlyToAnnual, isSwitchingAnnualToMonthly } from '../../util/plan-helpers'
import { track } from '../../services/analytics/analytics'
import { getAnalyticsProperties } from '../../util/analytics-helpers'

import { SubscriptionPlan } from '@vacationtracker/shared/types/company'
import { SubscriptionPeriod } from '@vacationtracker/shared/types/billing'

interface ISwitchPlanModal {
  openSwitchPlanModal: boolean
  currentPlan: SubscriptionPlan
  cancelPlanChange: () => void
  switchPlan: () => void
  newPlan: SubscriptionPlan
  newPeriod: SubscriptionPeriod
  currentPeriod: SubscriptionPeriod
  isLegacyPlan: boolean
  endDatePeriod: string
  submitLoader: boolean
  source?: string
  analyticsOverrides?: {
    activeUsers?: number
    stripePaymentStatus?: string
  }
}

export const SwitchPlanModal = ({
  openSwitchPlanModal,
  currentPlan,
  cancelPlanChange,
  switchPlan,
  newPlan,
  newPeriod,
  isLegacyPlan,
  currentPeriod,
  endDatePeriod,
  submitLoader,
  source,
  analyticsOverrides,
}: ISwitchPlanModal): React.ReactElement => {

  useEffect(() => {
    if (openSwitchPlanModal) {
      track('PLAN_SWITCH_MODAL_VIEWED', {
        ...getAnalyticsProperties(analyticsOverrides),
        plan: newPlan,
        source: source || 'billing_page',
      })
    }
  }, [openSwitchPlanModal, newPlan, source, analyticsOverrides])

  const getSwitchPlanMessage = () => {
    const isPlanUpgrade = isUpgrade(currentPlan, newPlan)
    const isPlanDowngrade = isDowngrade(currentPlan, newPlan)

    // Handle downgrades (Complete → Core, Complete → Freemium, Core → Freemium)
    if (isPlanDowngrade) {
      return <IntlMessages 
        id="components.switchPlanModal.downgradeMessage" 
        values={{
          currentPlan,
          nextPlan: newPlan,
          currentPeriod,
          nextPeriod: newPeriod,
          endDatePeriod,
          strong: (...chunks) => <strong>{chunks}</strong>,
        }}
      />
    }

    // Handle upgrades (Core → Complete, Freemium → Core, Freemium → Complete)
    if (isPlanUpgrade) {
      return <IntlMessages 
        id="components.switchPlanModal.upgradeImmediateMessage" 
        values={{
          currentPlan,
          newPlan,
          currentPeriod,
          newPeriod,
          strong: (...chunks) => <strong>{chunks}</strong>,
          legacyPlan: (...chunks) => isLegacyPlan ? chunks : '',
        }}
      />
    }

    // Handle period changes for same plan
    if (isSwitchingMonthlyToAnnual(currentPlan, newPlan, currentPeriod, newPeriod)) {
      return <IntlMessages 
        id="components.switchPlanModal.switchFromMonthlyToAnnually" 
        values={{
          newPlan,
          strong: (...chunks) => <strong>{chunks}</strong>,
          legacyPlan: (...chunks) => isLegacyPlan ? chunks : '',
        }} 
      />
    }

    if (isSwitchingAnnualToMonthly(currentPlan, newPlan, currentPeriod, newPeriod)) {
      return <IntlMessages 
        id="components.switchPlanModal.switchFromAnnuallyToMonthly" 
        values={{
          newPlan,
          endDatePeriod,
          strong: (...chunks) => <strong>{chunks}</strong>,
          legacyPlan: (...chunks) => isLegacyPlan ? chunks : '',
        }} 
      />
    }

    return null
  }

  return (
    <Modal
      title={<IntlMessages id="subscriptions.switchPlanTitle" values={{
        plan: newPlan,
        period: newPlan === 'Freemium' ? '' : newPeriod,
        strong: (...chunks) => <strong>{chunks}</strong>,
      }} />}
      centered
      open={openSwitchPlanModal}
      onOk={() => {
        track('PLAN_SWITCH_CONFIRMED', {
          ...getAnalyticsProperties(analyticsOverrides),
          plan: newPlan,
          source: source || 'billing_page',
        })
        switchPlan()
      }}
      okText={
        <IntlMessages id="app.switch" />
      }
      confirmLoading={submitLoader}
      onCancel={() => cancelPlanChange()}
      cancelText={<IntlMessages id="app.cancel" />}
    >
      {getSwitchPlanMessage()}
    </Modal>
  )
}

export default SwitchPlanModal
